/**
 * The changes of the API báo cáo page (docs/specs/reporting-api.md §5.3, §12): add or rename a place,
 * change what it receives, create, replace, revoke or unlock a key, and start a kind of data again.
 * What a place receives and new keys are access changes (a second person approves; a new key is
 * shown once when taken); revoking, unlocking, renaming and most restarts take effect at once.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError } from '../../../api';
import type { ReportingConsumer, ReportingKey, ReportingKeyPurpose } from '../../../api/areas/reporting';
import { Callout, Field } from '../../../components/blocks';
import { useWrite, WriteError } from '../../../components/Write';
import {
  EXPIRY_CHOICES,
  MAX_ADDRESSES,
  REPORTING_SCOPES,
  ROTATION_OVERLAPS,
  datasetsOf,
  expiryFor,
  missingParents,
  parseAddressList,
  resetNeedsApproval,
  scopeId,
  suspendReason,
  type ExpiryChoice,
} from '../../../lib/reportingApi';
import { ProposeForm } from '../../operator/AccessChanges';
import { FormFooter, ReasonField, ReasonForm, selectClass, type DialogBodyProps } from '../../operator/shared';

/** Labels of kinds of data, joined (`Khách hàng, Tên miền`). */
export function useScopeLabels() {
  const { t } = useTranslation('reporting');
  const scope = (s: string) => t(`scope.${scopeId(s)}.label`);
  const dataset = (d: string) => t(`dataset.${d as 'customers'}`, { defaultValue: d });
  return { scope, dataset, scopes: (list: readonly string[]) => list.map(scope).join(', '), datasets: (list: readonly string[]) => list.map(dataset).join(', ') };
}

// ---------------------------------------------------------------------------
// Add a place (a guided dialog: the place, what it receives, its first key)

export function AddPlaceBody({ close, changed, production }: DialogBodyProps & { production: boolean }) {
  const { t } = useTranslation('reporting');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [step, setStep] = useState<'place' | 'receive' | 'key'>('place');
  const [contactsPending, setContactsPending] = useState(false);
  const w = useWrite((b: { name: string; description: string | null }, opts) => api.createReportingConsumer(b, opts));
  const created = w.state.phase === 'done' ? w.state.result.consumer : null;

  if (!created) {
    const exists = w.state.phase === 'error' && isApiError(w.state.error) && w.state.error.code === 'CONFLICT';
    return (
      <form
        className="grid min-w-0 gap-4 [&>*]:min-w-0"
        onSubmit={(e) => {
          e.preventDefault();
          void w.run({ name: name.trim(), description: note.trim() || null }).then((r) => {
            if (r) {
              changed();
              setStep('receive');
            }
          });
        }}
      >
        <Field label={t('add.name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('add.namePlaceholder')} maxLength={100} required data-field="place-name" />
        </Field>
        <Field label={t('add.note')}>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} />
        </Field>
        {exists ? <Callout tone="warn">{t('add.exists')}</Callout> : w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
        <FormFooter busy={w.busy} disabled={!name.trim()} submit={t('add.submit')} onClose={close} />
      </form>
    );
  }

  return (
    <div className="grid min-w-0 gap-4 [&>*]:min-w-0">
      <Callout tone="ok">{t('add.done')}</Callout>
      <ol className="flex flex-wrap gap-2 text-xs font-medium text-muted-foreground" aria-label={created.name}>
        <li className={step === 'receive' ? 'text-foreground' : undefined}>{t('add.stepReceive')}</li>
        <li aria-hidden="true">›</li>
        <li className={step === 'key' ? 'text-foreground' : undefined}>{t('add.stepKey')}</li>
      </ol>
      {step === 'receive' ? (
        <ReceiveForm
          consumer={created}
          close={close}
          changed={changed}
          onProposed={(contacts) => setContactsPending(contacts)}
          after={(change) =>
            change.status !== 'PENDING' || change.can_self_approve ? (
              <div className="flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => setStep('key')} data-step="next-key">
                  {t('add.nextKey')}
                </Button>
              </div>
            ) : null
          }
          footerExtra={
            <Button type="button" variant="ghost" size="sm" onClick={() => setStep('key')}>
              {t('add.later')}
            </Button>
          }
        />
      ) : contactsPending ? (
        <>
          <Callout tone="info">{t('add.keyAfterContacts')}</Callout>
          <FormFooter busy={false} onClose={close} />
        </>
      ) : (
        <IssueKeyForm consumer={created} production={production} close={close} changed={changed} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// What a place receives

export function ReceiveForm({
  consumer,
  close,
  changed,
  after,
  onProposed,
  footerExtra,
}: DialogBodyProps & {
  consumer: ReportingConsumer;
  after?: Parameters<typeof ProposeForm>[0]['after'];
  onProposed?: (turnsContactsOn: boolean) => void;
  footerExtra?: ReactNode;
}) {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  const name = consumer.name;
  const [scopes, setScopes] = useState<string[]>(consumer.datasets);
  const [contacts, setContacts] = useState(consumer.contacts);
  const [weekly, setWeekly] = useState(consumer.weekly_resweep);
  const [confirmed, setConfirmed] = useState({ hosting: false, run_history: false, viewers: false });
  const turnsOn = contacts && !consumer.contacts;
  const turnsOff = !contacts && consumer.contacts;
  const removed = consumer.datasets.filter((s) => !scopes.includes(s));
  const missing = missingParents(scopes);
  const sorted = (l: readonly string[]) => [...l].sort().join();
  const changedAnything = sorted(scopes) !== sorted(consumer.datasets) || contacts !== consumer.contacts || weekly !== consumer.weekly_resweep;
  const ready = changedAnything && (!turnsOn || (confirmed.hosting && confirmed.run_history && confirmed.viewers));
  const toggle = (s: string, on: boolean) => setScopes((cur) => (on ? [...cur, s] : cur.filter((x) => x !== s)));

  return (
    <div className="grid min-w-0 gap-4 [&>*]:min-w-0">
      <ProposeForm
        close={close}
        changed={() => {
          changed();
          onProposed?.(turnsOn);
        }}
        base={{ kind: 'CHANGE_REPORTING_CONSUMER', consumer_id: consumer.id, datasets: [...scopes].sort(), contacts, weekly_resweep: weekly }}
        ready={ready}
        submit={t('receive.submit')}
        after={after}
        fields={
          <>
            <Field label={t('receive.kinds')} group>
              <div className="grid gap-2 sm:grid-cols-2" data-receive-kinds>
                {REPORTING_SCOPES.map((s) => (
                  <label key={s} className="flex items-start gap-2 rounded-md border p-2 text-sm" data-scope={scopeId(s)} aria-label={labels.scope(s)}>
                    <input type="checkbox" className="mt-1" checked={scopes.includes(s)} onChange={(e) => toggle(s, e.target.checked)} />
                    <span>
                      <span className="font-medium">{labels.scope(s)}</span>
                      <span className="block text-xs text-muted-foreground">{t(`scope.${scopeId(s)}.hint`)}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Field>
            {removed.length ? <Callout tone="warn">{t('receive.removed', { name, list: labels.scopes(removed) })}</Callout> : null}
            {missing.map((m) => (
              <Callout key={m.scope} tone="info">
                {t('receive.missing', { label: labels.scope(m.scope), parents: labels.scopes(m.missing), name })}
              </Callout>
            ))}
            <div className="grid gap-2 rounded-md border p-3">
              <label className="flex items-center justify-between gap-3 text-sm font-medium">
                <span>{t('receive.contacts')}</span>
                <Switch checked={contacts} onCheckedChange={setContacts} data-field="contacts" aria-label={t('receive.contacts')} />
              </label>
              <p className="text-xs text-muted-foreground">{t('receive.contactsHelp', { name })}</p>
              {turnsOn ? (
                <div className="grid gap-2 text-sm" data-contact-confirmations>
                  <span className="text-xs font-medium">{t('receive.confirmIntro')}</span>
                  {(['hosting', 'run_history', 'viewers'] as const).map((k) => (
                    <label key={k} className="flex items-start gap-2">
                      <input type="checkbox" className="mt-1" checked={confirmed[k]} onChange={(e) => setConfirmed((c) => ({ ...c, [k]: e.target.checked }))} />
                      <span>{t(`receive.confirm.${k}`, { name })}</span>
                    </label>
                  ))}
                </div>
              ) : null}
              {turnsOff ? <Callout tone="warn">{t('receive.contactsOff', { name })}</Callout> : null}
            </div>
            <div className="grid gap-1 rounded-md border p-3">
              <label className="flex items-center justify-between gap-3 text-sm font-medium">
                <span>{t('receive.weekly')}</span>
                <Switch checked={weekly} onCheckedChange={setWeekly} aria-label={t('receive.weekly')} />
              </label>
              <p className="text-xs text-muted-foreground">{t('receive.weeklyHint', { name })}</p>
            </div>
            {!changedAnything ? <p className="text-xs text-muted-foreground">{t('receive.noChange')}</p> : null}
          </>
        }
      />
      {footerExtra ? <div className="-mt-2 flex justify-start">{footerExtra}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Keys

function AddressField({ value, onChange, required, name, invalid, tooMany }: { value: string; onChange: (v: string) => void; required: boolean; name: string; invalid: string[]; tooMany: boolean }) {
  const { t } = useTranslation('reporting');
  return (
    <Field
      label={t('key.addresses')}
      hint={
        <>
          {t('key.addressesHelp')} {required ? t('key.addressesRequired', { name }) : t('key.addressesOptional')}
        </>
      }
    >
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={t('key.addressesPlaceholder')} className="font-mono text-xs" data-field="addresses" />
      {invalid.length ? <span className="text-xs text-critical">{t('key.addressesInvalid', { list: invalid.join(', ') })}</span> : null}
      {tooMany ? <span className="text-xs text-critical">{t('key.addressesTooMany', { max: MAX_ADDRESSES })}</span> : null}
    </Field>
  );
}

function ExpiryField({ value, onChange }: { value: ExpiryChoice; onChange: (v: ExpiryChoice) => void }) {
  const { t } = useTranslation('reporting');
  return (
    <Field label={t('key.expires')}>
      <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value as ExpiryChoice)} data-field="expiry">
        {EXPIRY_CHOICES.map((c) => (
          <option key={c} value={c}>
            {t(`key.expiry.${c}`)}
          </option>
        ))}
      </select>
    </Field>
  );
}

/** "Tạo khoá": name, use, addresses, expiry; approved, then taken once. */
export function IssueKeyForm({ consumer, production, close, changed }: DialogBodyProps & { consumer: ReportingConsumer; production: boolean }) {
  const { t } = useTranslation('reporting');
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState<ReportingKeyPurpose>('SCHEDULED');
  const [addresses, setAddresses] = useState('');
  const [expiry, setExpiry] = useState<ExpiryChoice>('1y');
  const parsed = parseAddressList(addresses);
  const required = production || consumer.contacts;
  const tooMany = parsed.addresses.length > MAX_ADDRESSES;
  const ready = Boolean(name.trim()) && parsed.invalid.length === 0 && !tooMany && (!required || parsed.addresses.length > 0);
  return (
    <ProposeForm
      close={close}
      changed={changed}
      base={{ kind: 'ISSUE_REPORTING_KEY', consumer_id: consumer.id, name: name.trim(), purpose, allowed_cidrs: parsed.addresses, expires_at: expiryFor(expiry) }}
      ready={ready}
      submit={t('receive.submit')}
      proposed={t('key.submitted')}
      fields={
        <>
          {consumer.contacts ? <Callout tone="warn">{t('key.contactsNote')}</Callout> : null}
          <Field label={t('key.name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('key.namePlaceholder')} maxLength={100} required data-field="key-name" />
          </Field>
          <Field label={t('key.purpose')} hint={t(`key.purposeHint.${purpose}`)}>
            <select className={selectClass} value={purpose} onChange={(e) => setPurpose(e.target.value as ReportingKeyPurpose)} data-field="purpose">
              <option value="SCHEDULED">{t('purpose.SCHEDULED')}</option>
              <option value="TRIGGERED">{t('purpose.TRIGGERED')}</option>
            </select>
          </Field>
          <AddressField value={addresses} onChange={setAddresses} required={required} name={consumer.name} invalid={parsed.invalid} tooMany={tooMany} />
          <ExpiryField value={expiry} onChange={setExpiry} />
        </>
      }
    />
  );
}

/** "Đổi khoá": a new key for the same use; the old one keeps working 1, 7 or 30 days. */
export function RotateKeyForm({ consumer, keyRow, production, close, changed }: DialogBodyProps & { consumer: ReportingConsumer; keyRow: ReportingKey; production: boolean }) {
  const { t } = useTranslation('reporting');
  const [name, setName] = useState(keyRow.name ?? '');
  const [addresses, setAddresses] = useState(keyRow.allowed_cidrs.join('\n'));
  const [expiry, setExpiry] = useState<ExpiryChoice>('1y');
  const [overlap, setOverlap] = useState<number>(7);
  const parsed = parseAddressList(addresses);
  const required = production || consumer.contacts;
  const tooMany = parsed.addresses.length > MAX_ADDRESSES;
  const ready = Boolean(name.trim()) && parsed.invalid.length === 0 && !tooMany && (!required || parsed.addresses.length > 0);
  return (
    <ProposeForm
      close={close}
      changed={changed}
      base={{ kind: 'ROTATE_REPORTING_KEY', credential_id: keyRow.id, name: name.trim(), allowed_cidrs: parsed.addresses, expires_at: expiryFor(expiry), overlap_days: overlap }}
      ready={ready}
      submit={t('receive.submit')}
      proposed={t('key.submitted')}
      fields={
        <>
          {consumer.contacts ? <Callout tone="warn">{t('key.contactsNote')}</Callout> : null}
          <Field label={t('key.name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required />
          </Field>
          <Field label={t('key.purpose')}>
            <Input value={keyRow.purpose ? t(`purpose.${keyRow.purpose}`) : '—'} readOnly disabled />
          </Field>
          <AddressField value={addresses} onChange={setAddresses} required={required} name={consumer.name} invalid={parsed.invalid} tooMany={tooMany} />
          <ExpiryField value={expiry} onChange={setExpiry} />
          <Field label={t('rotate.overlap')} hint={t('rotate.overlapHint', { name: consumer.name })}>
            <select className={selectClass} value={overlap} onChange={(e) => setOverlap(Number(e.target.value))}>
              {ROTATION_OVERLAPS.map((d) => (
                <option key={d} value={d}>
                  {t(`rotate.overlapDays.${String(d) as '7'}`)}
                </option>
              ))}
            </select>
          </Field>
        </>
      }
    />
  );
}

export function RevokeKeyForm({ consumer, keyRow, close, changed }: DialogBodyProps & { consumer: ReportingConsumer; keyRow: ReportingKey }) {
  const { t } = useTranslation('reporting');
  return (
    <ReasonForm
      close={close}
      changed={changed}
      warning={t('revoke.warning', { name: consumer.name })}
      submit={t('revoke.action')}
      danger
      run={(reason, opts) => api.revokeReportingKey(keyRow.id, { reason }, opts)}
      done={() => t('revoke.done')}
    />
  );
}

export function ResumeKeyForm({ keyRow, close, changed }: DialogBodyProps & { keyRow: ReportingKey }) {
  const { t } = useTranslation('reporting');
  return (
    <ReasonForm
      close={close}
      changed={changed}
      warning={
        <>
          {t('resume.why', { reason: t(`suspended.${suspendReason(keyRow.suspended_reason)}`) })} {t('resume.warning')}
        </>
      }
      submit={t('resume.submit')}
      run={(reason, opts) => api.resumeReportingKey(keyRow.id, { reason }, opts)}
      done={() => t('resume.done')}
    />
  );
}

// ---------------------------------------------------------------------------
// Rename

export function RenameForm({ consumer, close, changed }: DialogBodyProps & { consumer: ReportingConsumer }) {
  const { t } = useTranslation('reporting');
  const [name, setName] = useState(consumer.name);
  const [note, setNote] = useState(consumer.description ?? '');
  const w = useWrite((b: { name: string; description: string | null }, opts) => api.renameReportingConsumer(consumer.id, b, opts));
  const exists = w.state.phase === 'error' && isApiError(w.state.error) && w.state.error.code === 'CONFLICT';
  return (
    <form
      className="grid min-w-0 gap-4 [&>*]:min-w-0"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ name: name.trim(), description: note.trim() || null }).then((r) => r && changed());
      }}
    >
      <Field label={t('add.name')}>
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required />
      </Field>
      <Field label={t('add.note')}>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} />
      </Field>
      {exists ? <Callout tone="warn">{t('add.exists')}</Callout> : w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {w.state.phase === 'done' ? <Callout tone="ok">{t('rename.done')}</Callout> : null}
      <FormFooter busy={w.busy} disabled={!name.trim() || w.state.phase === 'done'} submit={t('signals.save')} onClose={close} />
    </form>
  );
}

// ---------------------------------------------------------------------------
// Sync again from the start

export function ResetForm({ consumer, close, changed }: DialogBodyProps & { consumer: ReportingConsumer }) {
  const { t } = useTranslation('reporting');
  const labels = useScopeLabels();
  const all = datasetsOf(consumer.datasets);
  const [picked, setPicked] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const approval = picked.filter((d) => resetNeedsApproval(consumer.contacts, d));
  const direct = picked.filter((d) => !resetNeedsApproval(consumer.contacts, d));
  // Restarts that send nothing personal take effect now; the others are proposed for approval.
  const w = useWrite(async (r: string, opts) => {
    for (const d of direct) await api.resetReportingSync(consumer.id, d, { reason: r }, { ...opts, idempotencyKey: `${opts.idempotencyKey ?? ''}-${d}` });
    for (const d of approval) await api.proposeAccessChange({ kind: 'RESET_REPORTING_SYNC', consumer_id: consumer.id, dataset: d, reason: r }, { ...opts, idempotencyKey: `${opts.idempotencyKey ?? ''}-${d}` });
    return { direct, approval };
  });
  const done = w.state.phase === 'done' ? w.state.result : null;
  return (
    <form
      className="grid min-w-0 gap-4 [&>*]:min-w-0"
      onSubmit={(e) => {
        e.preventDefault();
        void w.run(reason.trim()).then((r) => r && changed());
      }}
    >
      <Callout tone="warn">{t('reset.warning', { name: consumer.name })}</Callout>
      <Field label={t('reset.pick')} group>
        <div className="grid gap-2 sm:grid-cols-2" data-reset-kinds>
          {all.map((d) => (
            <label key={d} className="flex items-center gap-2 text-sm" data-dataset={d}>
              <input type="checkbox" checked={picked.includes(d)} disabled={Boolean(done)} onChange={(e) => setPicked((cur) => (e.target.checked ? [...cur, d] : cur.filter((x) => x !== d)))} />
              {labels.dataset(d)}
            </label>
          ))}
        </div>
      </Field>
      {approval.length ? <Callout tone="warn">{t('reset.contactsWarning')}</Callout> : null}
      {!done ? <ReasonField value={reason} onChange={setReason} /> : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      {done ? (
        <Callout tone="ok">
          {done.direct.length ? <p>{t('reset.done', { list: labels.datasets(done.direct) })}</p> : null}
          {done.approval.length ? <p>{t('reset.proposed', { list: labels.datasets(done.approval) })}</p> : null}
        </Callout>
      ) : null}
      <FormFooter busy={w.busy} disabled={!reason.trim() || picked.length === 0} submit={done ? undefined : t('reset.submit')} onClose={close} />
    </form>
  );
}
